/**
 * useWalkthroughTarget — attaches a ref to a component and, when the walkthrough
 * reaches that component's step, measures its screen position and registers it as
 * the spotlight target.
 *
 * Usage:
 *   const { viewRef, registerTarget } = useWalkthroughTarget(WALKTHROUGH_STEPS.CREATE_BTN)
 *   // attach viewRef to the View, call registerTarget() inside a useEffect when ready
 */

import { useRef, useCallback } from 'react'
import { View } from 'react-native'
import { useGuestWalkthroughStore } from '../stores/guestWalkthroughStore'
import {
  SPOTLIGHT_PADDING,
} from '../../create/constants/guestWalkthrough.constants'
import type { WalkthroughStepKey } from '../../create/types/guestWalkthrough.types'

interface UseWalkthroughTargetReturn {
  viewRef: React.RefObject<View | null>
  registerTarget: () => void
}

export function useWalkthroughTarget(stepKey: WalkthroughStepKey): UseWalkthroughTargetReturn {
  const viewRef = useRef<View>(null)
  const currentStep = useGuestWalkthroughStore((s) => s.currentStep)
  const setTargetRect = useGuestWalkthroughStore((s) => s.setTargetRect)

  const registerTarget = useCallback(() => {
    if (currentStep !== stepKey) return
    requestAnimationFrame(() => {
      // Guard against stale async callbacks: step might change before measure runs.
      if (useGuestWalkthroughStore.getState().currentStep !== stepKey) return
      viewRef.current?.measureInWindow((x, y, width, height) => {
        // Guard against stale async callbacks: step might change before measure completes.
        if (useGuestWalkthroughStore.getState().currentStep !== stepKey) return
        // Guard: skip if the component is unmounted or not yet laid out
        if (width === 0 && height === 0) return
        setTargetRect({
          x: x - SPOTLIGHT_PADDING,
          y: y - SPOTLIGHT_PADDING,
          width: width + SPOTLIGHT_PADDING * 2,
          height: height + SPOTLIGHT_PADDING * 2,
        })
      })
    })
  }, [currentStep, stepKey, setTargetRect])

  return { viewRef, registerTarget }
}
